import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import {
  Accordion,
  AccordionButton,
  AccordionItem,
  AccordionPanel,
} from "@reach/accordion";
import { css } from "@nju/css";
import { IArticle } from "@nju/data-access-portal-help";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import {
  A,
  BlockText,
  Heading,
  Loader,
  Markdown,
  Separator,
  Text,
} from "@nju/ui";
import { formatAddress, getSessionAddress, IAddress } from "@nju/utils";

interface IProps {
  isOk: boolean;
  article?: IArticle;
  isLoading?: boolean;
  variant?: IScope;
}

const formalErrorCodes = new Set(["50186", "50126", "50122"]);

export function Summary({
  article,
  isLoading,
  isOk,
  children,
  variant = "portal",
}: React.PropsWithChildren<IProps>) {
  const router = useRouter();
  const params = salesRoutes.sprawdzDostepnosc.uslugaNiedostepna
    .input(router.query)
    .unwrap();

  const address: IAddress | undefined = React.useMemo(() => {
    const address = getSessionAddress("installationAddress");

    return {
      street: address?.streetName || undefined,
      streetNumber: address?.houseNumber,
      municipality: address?.cityName,
      apartmentNumber: address?.apartmentNumber,
    };
  }, []);

  const mapErrorCode = (errorCode: string | number | undefined) => {
    if (errorCode === "50130") {
      return "Na tym adresie nie możemy zaoferować usługi nju światłowód, ponieważ jest już aktywna usługa Orange lub nju światłowód";
    }

    if (errorCode && formalErrorCodes.has(errorCode.toString())) {
      return (
        <BlockText size="l">
          Na tym adresie nie możemy zaoferować usługi nju światłowód, ponieważ
          nie spełnia on kryteriów formalnych (błąd {errorCode}) – szczegóły{" "}
          <Link
            className="underline"
            href="https://esklep.online.orange.pl/omnibook/nju-swiatlow-1#proces-sprzedazy#informacje-podstawowe"
            rel="noreferrer"
            target="_blank"
          >
            (link)
          </Link>
        </BlockText>
      );
    }

    return "Cały czas zwiększamy zasięg dostępności usługi nju światłowód, dlatego warto co jakiś czas sprawdzać, czy usługa jest już dostępna we wskazanej lokalizacji.";
  };

  React.useEffect(() => {
    if (!address) {
      router.replace(portalRoutes.sprawdzDostepnosc.index.get());
    }
  }, [router, address]);

  if (!address) {
    // eslint-disable-next-line unicorn/no-null
    return null;
  }

  return (
    <div>
      <div className={styles.availableStatus()}>
        <Heading font="ttfirs" tone="neutral" variant="h2" weight="bold">
          {isOk ? "usługa jest dostępna" : "usługa jest niedostępna"}
        </Heading>
      </div>
      <Text size="l" tone="accent">
        we wskazanej lokalizacji:
      </Text>
      <br />
      <Text
        className={styles.address()}
        size="l"
        testId="unavailable-service-installation-location"
        tone="accent"
      >
        {formatAddress(address)}
      </Text>
      {isOk ? (
        <BlockText
          className={styles.description()}
          size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
        >
          kontynuuj zakup usługi przez stronę internetową,
          <br /> klikając „dalej”
        </BlockText>
      ) : (
        <div data-cy="service-location-info">
          <BlockText
            className={styles.description()}
            size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
          >
            {variant !== "sales" && (
              <>
                <Text size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
                  Cały czas pracujemy nad zwiększeniem zasięgu usługi nju
                  światłowód. Sprawdź już teraz, czy jesteś{" "}
                  <Link
                    href="https://www.orange.pl/internet/internet-domowy#WWTComponent"
                    passHref={true}
                    target="_blank"
                  >
                    <A
                      display="inline"
                      size="inline"
                      variant="neutral"
                      withUnderline={true}
                    >
                      <Text
                        size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
                        weight="bold"
                      >
                        w zasięgu Światłowodu Orange.
                      </Text>
                    </A>
                  </Link>
                </Text>
                <Separator size="s" tone="invisible" />
                <Text size={{ "@onlyMobile": "m", "@fromTablet": "l" }}>
                  A może nie światłowód? Kup Internet mobilny od nju z nawet 300
                  GB za 29 zł/mies. i bądź online gdziekolwiek jesteś. Kliknij{" "}
                  <Link
                    href="https://www.njumobile.pl/oferta/internet"
                    passHref={true}
                    target="_blank"
                  >
                    <A
                      display="inline"
                      size="inline"
                      variant="neutral"
                      withUnderline={true}
                    >
                      <Text
                        size={{ "@onlyMobile": "m", "@fromTablet": "l" }}
                        weight="bold"
                      >
                        tutaj
                      </Text>
                    </A>
                  </Link>{" "}
                  i sprawdź szczegóły oferty.
                </Text>
              </>
            )}
            {variant === "sales" && mapErrorCode(params?.errorCode)}
          </BlockText>
        </div>
      )}

      {variant !== "sales" && !isOk && isLoading && <Loader />}

      {variant !== "sales" && !isOk && !isLoading && article && (
        <>
          <Separator size="s" tone="invisible" />
          <Accordion collapsible={true}>
            <AccordionItem>
              <AccordionButton
                className={styles.trigger()}
                data-cy="expand-location-info"
              >
                {article.title.pl}
              </AccordionButton>
              <AccordionPanel
                className={styles.accordion()}
                data-cy="faq-section"
              >
                <Markdown className="prose" content={article.content.pl} />
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </>
      )}

      <div className={styles.buttonWrapper()}>{children}</div>
    </div>
  );
}

const styles = {
  availableStatus: css({
    mb: "$15",
    "@fromTablet": {
      mb: "$30",
    },
  }),
  description: css({
    pt: "$15",
  }),
  buttonWrapper: css({
    "@onlyMobile": {
      my: "$20",
    },
  }),
  address: css({
    textTransform: "capitalize",
  }),
  trigger: css({
    textAlign: "left",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    textDecoration: "underline",
    pb: "$10",
  }),
  accordion: css({
    "p + p": {
      pt: "$5",
    },
  }),
};
